import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Typography } from '@mui/material';
import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { TrackedIconButton as IconButton } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import { useImages } from '../../../hooks/images';
import imageNA from '../../../images/image_not_available.png';
import { RTLDirectionContext } from '../../Layout';
import DriverCard from './DriverCard';
import { CARD_HEIGHT, CARD_MARGIN } from './DriverCard';
import { DriverCardProps } from './DriverCard';
import DriverModal from './DriverModal';

export const NAV_BUTTON_WIDTH = 50;

export interface DriverGridProps {
    drivers: Array<DriverCardProps>;
    cardRows: number;
    cardColumns: number;
    ref?: React.ForwardedRef<HTMLDivElement>;
}

const DriverGrid: React.FC<DriverGridProps> = forwardRef<HTMLDivElement, DriverGridProps>((props, ref) => {
    const { t } = useTranslation();
    const { driverToken } = useParams<{ driverToken: string }>();
    const numberOfCards = props.cardRows * props.cardColumns;
    let initialIndex = 0;
    const driverIndex = props.drivers.findIndex((d) => d.driver.token === parseInt(driverToken));
    if (driverIndex >= 0) {
        initialIndex = Math.floor(driverIndex / numberOfCards) * numberOfCards;
    }
    const [startingIndex, setStartingIndex] = useState<number>(initialIndex);
    const [selectedDriverToken, setSelectedDriverToken] = useState<number>();
    const cardsOnScreen = props.cardColumns * props.cardRows;
    const { api, agencyApi } = useApi();
    const getImage = (eventStr: string) => {
        const [eventIdStr, type] = eventStr.split(':');
        const eventId = parseInt(eventIdStr);
        if (type === 'accident') {
            return agencyApi.agencyV0EventEventIdImageGet({ eventId }, { responseType: 'blob' });
        }
        return api.apiV0EventEventIdImageGet({ eventId }, { responseType: 'blob' });
    };
    const [eventImages, addToQueue] = useImages(cardsOnScreen, getImage);
    const [defaultTab, setDefaultTab] = useState<number>(0);
    const isRTL = useContext(RTLDirectionContext);
    const history = useHistory();
    const isSingleColumn = props.cardColumns === 1;

    useEffect(() => {
        const eventKeys = props.drivers
            .slice(startingIndex, startingIndex + 2 * cardsOnScreen)
            .filter((d) => !!d.mostRecentEvent)
            .map((d) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const eventId = d.mostRecentEvent!.event_id;
                if (d.accidentEventIds.includes(eventId)) {
                    return `${eventId}:accident`;
                }
                return `${eventId}:event`;
            });
        addToQueue(eventKeys);
    }, [props.drivers, props.cardRows, props.cardColumns, startingIndex]);

    useEffect(() => {
        if (driverToken) {
            const vehicleExists = props.drivers.some((d) => d.driver.token === parseInt(driverToken));
            if (vehicleExists) {
                setSelectedDriverToken(parseInt(driverToken));
            } else {
                history.replace('/drivers');
            }
        } else {
            setSelectedDriverToken(undefined);
        }
    }, [driverToken, props.drivers]);

    useEffect(() => {
        setStartingIndex(0);
    }, [props.drivers.length]);

    useEffect(() => {
        window.addEventListener('keydown', keyScroll);
        return () => window.removeEventListener('keydown', keyScroll);
    });

    const keyMap: Record<string, 'previous' | 'next'> = {
        ArrowLeft: isRTL ? 'next' : 'previous',
        ArrowRight: isRTL ? 'previous' : 'next',
    };

    const keyScroll = (e: KeyboardEvent) => {
        if (e.key in keyMap && selectedDriverToken === undefined) {
            e.preventDefault();
            if (!e.repeat) {
                turnPage(keyMap[e.key]);
            }
        }
    };

    const turnPage = (direction: 'previous' | 'next') => {
        if (direction === 'previous' && startingIndex > 0) {
            setStartingIndex((prev) => Math.max(0, prev - numberOfCards));
        } else if (direction === 'next' && startingIndex + cards.length < props.drivers.length) {
            setStartingIndex((prev) => prev + numberOfCards);
        }
    };

    const cards = props.drivers.slice(startingIndex, startingIndex + numberOfCards).map((card, idx) => {
        return (
            <Box
                id={`${card.licensePlate}-card`}
                key={idx}
                aria-label="vehicle-card"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <DriverCard
                    timestamp={card.timestamp}
                    eventIds={card.eventIds}
                    accidentEventIds={card.accidentEventIds}
                    driverName={card.driverName}
                    licensePlate={card.licensePlate}
                    subFleet={card.subFleet}
                    score={card.score}
                    behaviors={card.behaviors}
                    mostRecentEvent={card.mostRecentEvent}
                    image={
                        !!card.mostRecentEvent
                            ? eventImages[
                                  `${card.mostRecentEvent.event_id}:${
                                      card.accidentEventIds.includes(card.mostRecentEvent.event_id)
                                          ? 'accident'
                                          : 'event'
                                  }`
                              ]
                            : imageNA
                    }
                    setDefaultTab={setDefaultTab}
                    inactive={card.inactive}
                    unknown={card.unknown}
                    driver={card.driver}
                />
            </Box>
        );
    });

    const previousBtn = (
        <Box sx={isSingleColumn ? { mr: 1 } : { gridColumn: '1', gridRow: `1 / ${props.cardRows + 1}` }}>
            <IconButton
                id="previous-btn"
                size="large"
                onClick={() => turnPage('previous')}
                disabled={startingIndex === 0}
            >
                {isRTL ? (
                    <NavigateNextIcon sx={{ transform: 'scale(2)' }} />
                ) : (
                    <NavigateBeforeIcon sx={{ transform: 'scale(2)' }} />
                )}
            </IconButton>
        </Box>
    );

    const nextBtn = (
        <Box
            sx={
                isSingleColumn
                    ? undefined
                    : { gridColumn: `${props.cardColumns + 2}`, gridRow: `1 / ${props.cardRows + 1}` }
            }
        >
            <IconButton
                id="next-btn"
                size="large"
                onClick={() => turnPage('next')}
                disabled={startingIndex + cards.length === props.drivers.length}
            >
                {isRTL ? (
                    <NavigateBeforeIcon sx={{ transform: 'scale(2)' }} />
                ) : (
                    <NavigateNextIcon sx={{ transform: 'scale(2)' }} />
                )}
            </IconButton>
        </Box>
    );

    const selectedDriver = props.drivers.find((d) => d.driver.token === selectedDriverToken);

    return (
        <>
            {selectedDriver ? (
                <DriverModal driver={selectedDriver} onClose={() => history.push('/drivers')} initialTab={defaultTab} />
            ) : null}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    minHeight: `${CARD_HEIGHT + 2 * CARD_MARGIN + 45}px`,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: isSingleColumn
                            ? '1fr'
                            : `${NAV_BUTTON_WIDTH}px repeat(${props.cardColumns}, 1fr) ${NAV_BUTTON_WIDTH}px`,
                        gridTemplateRows: `repeat(${props.cardRows}, 1fr)`,
                        height: 'calc(100% - 45px)',
                        alignItems: 'center',
                        alignContent: 'center',
                        position: 'relative',
                    }}
                    ref={ref}
                >
                    {isSingleColumn ? (
                        <>{cards}</>
                    ) : (
                        <>
                            {previousBtn}
                            {cards}
                            {nextBtn}
                        </>
                    )}
                </Box>
                {props.drivers.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: isSingleColumn ? 1 : 0 }}>
                        {isSingleColumn ? (
                            <Box sx={{ width: '100%', mb: 0.5, display: 'flex', justifyContent: 'center' }}>
                                {previousBtn}
                                {nextBtn}
                            </Box>
                        ) : null}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Typography variant="overline" sx={{ lineHeight: 1 }}>
                                {t('content.safety.vehicles_shown', {
                                    start: startingIndex + 1,
                                    end: startingIndex + cards.length,
                                    total: props.drivers.length,
                                })}
                            </Typography>
                        </Box>
                    </Box>
                ) : null}
            </Box>
        </>
    );
});

DriverGrid.displayName = 'DriverGrid';
export default DriverGrid;
